import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFilm, FiVideo, FiUpload, FiDownload, FiRefreshCw, FiLoader, FiVolumeX } from 'react-icons/fi';
import SEO from '../components/SEO';
import { videoAudioRemoverSchemas } from '../schemas';
import API_BASE_URL from '../config';
import './VideoAudioRemover.css';

const VideoAudioRemover = () => {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const outputVideoRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [outputVideoUrl, setOutputVideoUrl] = useState(null);
  const [outputVideoName, setOutputVideoName] = useState('');
  const [outputVideoSize, setOutputVideoSize] = useState(0);
  const [isRemovingAudio, setIsRemovingAudio] = useState(false);
  const [removeProgress, setRemoveProgress] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    return () => {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    };
  }, [videoPreviewUrl]);

  useEffect(() => {
    return () => {
      if (outputVideoUrl) URL.revokeObjectURL(outputVideoUrl);
    };
  }, [outputVideoUrl]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndSetVideo(file);
    }
  };

  const validateAndSetVideo = (file) => {
    if (!file.type.startsWith('video/')) {
      setError('Please upload a valid video file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      setError('Video file size should be less than 100MB');
      return;
    }

    setError('');
    const url = URL.createObjectURL(file);
    setVideoPreviewUrl(url);
    setVideoFile(file);
    setOutputVideoUrl(null);
    setOutputVideoName('');
    setOutputVideoSize(0);
    setRemoveProgress(0);

    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url;
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      validateAndSetVideo(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removeAudio = async () => {
    if (!videoFile) return;

    let progressInterval = null;
    setIsRemovingAudio(true);
    setRemoveProgress(0);
    setError('');
    setOutputVideoName('');
    setOutputVideoSize(0);

    if (outputVideoUrl) {
      URL.revokeObjectURL(outputVideoUrl);
    }
    setOutputVideoUrl(null);

    try {
      const formData = new FormData();
      formData.append('video', videoFile);

      progressInterval = setInterval(() => {
        setRemoveProgress((previous) => Math.min(previous + 5, 90));
      }, 200);

      const response = await fetch(`${API_BASE_URL}/api/remove-video-audio`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Conversion failed');
      }

      const videoBytes = Uint8Array.from(atob(data.mp4), (character) => character.charCodeAt(0));
      const videoBlob = new Blob([videoBytes], { type: 'video/mp4' });

      setOutputVideoUrl(URL.createObjectURL(videoBlob));
      setOutputVideoName(data.filename);
      setOutputVideoSize(data.outputSize || videoBlob.size);
      setRemoveProgress(100);
    } catch (err) {
      console.error('Video audio removal error:', err);
      setError(err.message || 'Failed to remove audio. Please try again.');
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setIsRemovingAudio(false);
    }
  };

  const downloadOutputVideo = () => {
    if (!outputVideoUrl) return;

    const link = document.createElement('a');
    link.href = outputVideoUrl;
    const videoName = videoFile ? videoFile.name.replace(/\.[^/.]+$/, '') : 'video';
    link.download = `${videoName}-no-audio.mp4`;
    link.click();
  };

  const resetConverter = () => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    if (outputVideoUrl) URL.revokeObjectURL(outputVideoUrl);

    setVideoFile(null);
    setVideoPreviewUrl(null);
    setOutputVideoUrl(null);
    setOutputVideoName('');
    setOutputVideoSize(0);
    setError('');
    setRemoveProgress(0);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (outputVideoRef.current) {
      outputVideoRef.current.pause();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const index = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, index)).toFixed(2)) + ' ' + sizes[index];
  };

  return (
    <div className="video-audio-remover-page">
      <SEO 
        title="Remove Audio from Video Online Free – Mute Video"
        description="Mute or remove audio track from any video online free. Fast, simple tool to strip sound from MP4, MOV & more."
        keywords="remove audio from video, mute video online, delete video audio"
        path="/video-audio-remover"
        schema={videoAudioRemoverSchemas}
      />
      <section className="tool-hero">
        <div className="tool-hero-container">
          <motion.div
            className="tool-icon-large"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <FiVolumeX />
          </motion.div>

          <motion.h1
            className="tool-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Video Audio Remover
          </motion.h1>

          <motion.p
            className="tool-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Remove audio from your videos and download a silent MP4. Upload videos up to 100MB.
          </motion.p>
        </div>
      </section>

      <section className="tool-workspace">
        <div className="workspace-container">
          <AnimatePresence mode="wait">
            {!videoFile ? (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="upload-zone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <motion.div
                  className="upload-icon"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <FiUpload />
                </motion.div>
                <h3>Upload Your Video</h3>
                <p>Drag and drop or click to browse (up to 100MB)</p>
                <p className="supported-formats">Supported: MP4, MOV, AVI, WebM, MKV</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </motion.div>
            ) : (
              <motion.div
                key="converter"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="converter-workspace"
              >
                <div className="video-audio-grid">
                  <motion.div
                    className="preview-card-minimal"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="card-header-small">
                      <FiFilm />
                      <span>Original Video</span>
                    </div>
                    <div className="card-content">
                      <video
                        key={videoPreviewUrl}
                        ref={videoRef}
                        src={videoPreviewUrl}
                        controls
                        playsInline
                        preload="auto"
                        className="video-preview"
                        onLoadedData={() => console.log('Video ready to play')}
                        onError={() => {
                          setError('Failed to load video. Please try another file.');
                        }}
                      />
                    </div>
                    <div className="video-info-bar">
                      <span className="video-info-name">{videoFile ? videoFile.name : ''}</span>
                      <span className="video-info-size">{videoFile ? formatFileSize(videoFile.size) : ''}</span>
                    </div>
                  </motion.div>

                  <motion.div
                    className="preview-card-minimal"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="card-header-small">
                      <FiVideo />
                      <span>Silent Video Output</span>
                    </div>
                    <div className="card-content">
                      <AnimatePresence mode="wait">
                        {isRemovingAudio ? (
                          <motion.div
                            key="removing-audio"
                            className="loading-circle"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <FiLoader className="spinning" />
                            <span>Removing audio... {removeProgress}%</span>
                          </motion.div>
                        ) : outputVideoUrl ? (
                          <motion.div
                            key="output-video"
                            className="video-result-minimal"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                          >
                            <video
                              key={outputVideoUrl}
                              ref={outputVideoRef}
                              src={outputVideoUrl}
                              controls
                              playsInline
                              preload="auto"
                              className="video-preview"
                            />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="waiting"
                            className="waiting-minimal"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                          >
                            <FiVideo />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <div className="video-info-bar">
                      <span className="video-info-name">{outputVideoName || 'Waiting for output'}</span>
                      <span className="video-info-size">{outputVideoSize ? formatFileSize(outputVideoSize) : ''}</span>
                    </div>
                  </motion.div>
                </div>

                {error && (
                  <motion.div
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {error}
                  </motion.div>
                )}

                <motion.div
                  className="action-buttons-row"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  {!outputVideoUrl ? (
                    <motion.button
                      className="btn-primary-large"
                      onClick={removeAudio}
                      disabled={isRemovingAudio}
                      whileHover={{ scale: isRemovingAudio ? 1 : 1.05 }}
                      whileTap={{ scale: isRemovingAudio ? 1 : 0.95 }}
                    >
                      {isRemovingAudio ? (
                        <>
                          <FiLoader className="spinning" />
                          Removing audio... {removeProgress}%
                        </>
                      ) : (
                        <>
                          <FiVolumeX />
                          Remove Audio
                        </>
                      )}
                    </motion.button>
                  ) : (
                    <>
                      <motion.button
                        className="btn-primary-large"
                        onClick={downloadOutputVideo}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring' }}
                      >
                        <FiDownload />
                        Download Silent Video
                      </motion.button>
                      <motion.button
                        className="btn-secondary"
                        onClick={resetConverter}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FiRefreshCw />
                        Start Over
                      </motion.button>
                    </>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <section className="info-section">
        <div className="info-container">
          <motion.div
            className="info-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3>Why Remove Audio?</h3>
            <ul>
              <li>Create silent videos for presentations and social media</li>
              <li>Remove background noise while keeping the original visuals</li>
              <li>Prepare clips for adding a new voiceover or soundtrack</li>
              <li>Share videos safely in audio-restricted environments</li>
              <li>Keep the output in a widely compatible MP4 format</li>
            </ul>
          </motion.div>

          <motion.div
            className="info-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3>Silent MP4 Output</h3>
            <ul>
              <li>Original video visuals are preserved</li>
              <li>All audio streams are removed from the output</li>
              <li>Optimized for playback across devices and browsers</li>
              <li>Fast server-side processing with automatic cleanup</li>
              <li>Preview and download the result before starting over</li>
            </ul>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default VideoAudioRemover;
